/**
 * Laufende KIAN-Antworten je Gespräch.
 *
 * Der Store zeigt immer nur EIN Gespräch (`currentSession`), Antworten laufen aber in mehreren
 * gleichzeitig. Was unterwegs ist, steht deshalb je Gespräch hier — nicht als ein Flag für alle:
 * Bis v1.381.0 zeigte jedes Gespräch „KIAN tippt" und sperrte die Eingabe, sobald irgendwo eine
 * Antwort lief, und wer zurückwechselte, sah die Antwort nicht, bis er neu lud (der Stand vom
 * Server kennt sie erst, wenn sie fertig ist).
 */

import type { ChatMessage } from '../lib/api/domains/assistant';

export interface PendingReply {
  /** Kennung dieser Antwort — Nachzügler einer früheren Antwort fassen eine neue nicht an */
  token: string;
  /** Gestreamt oder als ein Stück (Datei-Upload, der Multipart-Endpunkt streamt nicht) */
  kind: 'stream' | 'upload';
  /** Die Nachricht des Technikers, so wie sie optimistisch angezeigt wird */
  userMessage: ChatMessage;
  /** Platzhalter bzw. bisheriger Text der Antwort; nach `done` die gespeicherte Antwort */
  assistantMessage: ChatMessage | null;
  /** Wann gesendet wurde (Client-Uhr) */
  startedAt: number;
  /**
   * Antwort ist fertig und gespeichert. Der Eintrag bleibt, bis ein vom Server geladener Stand sie
   * enthält: Ein Abruf, der vor dem Speichern losging, kennt sie sonst nicht.
   */
  done: boolean;
  isAnalyzingFiles: boolean;
  uploadProgress: number | null;
}

/** Wartet dieses Gespräch auf KIAN? */
export function isReplyBusy(reply: PendingReply | undefined): boolean {
  return !!reply && !reply.done;
}

/** Wie viele Gespräche gerade auf KIAN warten (Flyout-Knopf, „neue Antwort"-Zähler) */
export function countBusyReplies(replies: Record<string, PendingReply>): number {
  let n = 0;
  for (const r of Object.values(replies)) if (!r.done) n += 1;
  return n;
}

/**
 * Gespräche mit laufender Antwort als ein Text (sortierte IDs, Komma) — für Zustand-Selektoren:
 * ein neues Objekt je Textstück ließe Listen bei jedem Wort neu rendern.
 */
export function busyReplyKey(replies: Record<string, PendingReply>): string {
  return Object.keys(replies)
    .filter((id) => !replies[id].done)
    .sort()
    .join(',');
}

/** Spielraum für die Uhr des Geräts gegenüber dem Server */
const CLOCK_SKEW_MS = 5 * 60_000;
/** So viel speichert der Server vom Text einer Nachricht */
const SERVER_CONTENT_LIMIT = 8000;

/**
 * Hat der Server die Nachricht des Technikers schon gespeichert? Er legt sie gleich zu Beginn an —
 * unter eigener ID, die der Client nicht kennt, deshalb Vergleich über Text und Zeitpunkt.
 */
function serverHasUserMessage(messages: ChatMessage[], reply: PendingReply): boolean {
  const content = reply.userMessage.content.slice(0, SERVER_CONTENT_LIMIT);
  return messages.some((m) => {
    if (m.role !== 'USER') return false;
    if (m.id === reply.userMessage.id) return true;
    if (m.content !== content) return false;
    const at = Date.parse(m.createdAt);
    return Number.isNaN(at) || at >= reply.startedAt - CLOCK_SKEW_MS;
  });
}

/**
 * Setzt eine laufende (oder gerade fertig gewordene) Antwort in einen frisch geladenen Stand ein.
 * `settled` = der Server kennt die fertige Antwort schon, der Eintrag kann weg.
 */
export function mergePendingReply(
  messages: ChatMessage[],
  reply: PendingReply | undefined,
): { messages: ChatMessage[]; settled: boolean } {
  if (!reply) return { messages, settled: false };
  const assistantId = reply.assistantMessage?.id;
  if (reply.done && assistantId && messages.some((m) => m.id === assistantId)) {
    return { messages, settled: true };
  }
  const next = [...messages];
  if (!serverHasUserMessage(messages, reply)) next.push(reply.userMessage);
  if (reply.assistantMessage && !messages.some((m) => m.id === assistantId)) next.push(reply.assistantMessage);
  return { messages: next, settled: false };
}
